/**
 * Optimistic pending-move store for drag-drop between Spaces.
 *
 * Holds a map of participantId -> move entry while a drag-drop has been sent
 * to the server but the socket echo has not yet arrived.
 *
 * spaceId conventions used here:
 *   - real space: String(space.id)  (numeric string e.g. "42")
 *   - lounge:     'lobby'
 *
 * All timers are kept in a separate _timers map so they are never stored
 * inside the move entry (keeps entries serialisable and avoids circular refs).
 */

import { CONV_STORAGE, spacesView } from '../../libs/roomnew';

/** @type {Object.<string, {participantId:string, targetSpaceId:string, sourceSpaceId:string, confirmed:boolean}>} */
const _moves = {};

/** @type {Object.<string, {revertTimer:number|null, cleanupTimer:number|null}>} */
const _timers = {};

/**
 * Re-render the tiles for a given spaceId ('lobby' or a numeric string).
 */
function _renderSpace(spaceId) {
    try {
        if (String(spaceId) === 'lobby') {
            spacesView.renderLobbySpaceTiles();
        } else {
            const space = CONV_STORAGE.getSpace(parseInt(spaceId));
            if (space && typeof space.renderSpaceTiles === 'function') {
                space.renderSpaceTiles();
            }
        }
    } catch (e) {
        console.warn('[PendingMoves] _renderSpace error', e);
    }
}

/**
 * Register an optimistic move immediately after the drag-drop fires.
 * Starts a 6 s revert timer that calls removePendingMove() if the server
 * never confirms.
 *
 * @param {{participantId:string|number, targetSpaceId:string|number, sourceSpaceId:string|number}} opts
 */
export function registerPendingMove({ participantId, targetSpaceId, sourceSpaceId }) {
    try {
        const pid = String(participantId);
        const src = String(sourceSpaceId);
        const tgt = String(targetSpaceId);

        // Clear any pre-existing timers for this participant.
        if (_timers[pid]) {
            clearTimeout(_timers[pid].revertTimer);
            clearTimeout(_timers[pid].cleanupTimer);
        }

        _moves[pid] = { participantId: pid, targetSpaceId: tgt, sourceSpaceId: src, confirmed: false };

        const revertTimer = setTimeout(() => {
            removePendingMove(pid);
        }, 6000);

        _timers[pid] = { revertTimer, cleanupTimer: null };

        _renderSpace(src);
        _renderSpace(tgt);
    } catch (e) {
        console.warn('[PendingMoves] registerPendingMove error', e);
    }
}

/**
 * Called when the server confirms the move (Space.participantJoined fires on
 * the target space). Cancels the revert timer, marks the entry as confirmed,
 * and starts a short 4 s cleanup timer so the pending state outlives the
 * participantLeft echo on the source space.
 *
 * @param {string|number} participantId
 */
export function confirmPendingMove(participantId) {
    try {
        const pid = String(participantId);
        const entry = _moves[pid];
        if (!entry || entry.confirmed) return;

        if (_timers[pid] && _timers[pid].revertTimer) {
            clearTimeout(_timers[pid].revertTimer);
        }

        entry.confirmed = true;

        const cleanupTimer = setTimeout(() => {
            removePendingMove(pid);
        }, 4000);

        if (_timers[pid]) {
            _timers[pid].revertTimer = null;
            _timers[pid].cleanupTimer = cleanupTimer;
        } else {
            _timers[pid] = { revertTimer: null, cleanupTimer };
        }

        _renderSpace(entry.sourceSpaceId);
        _renderSpace(entry.targetSpaceId);
    } catch (e) {
        console.warn('[PendingMoves] confirmPendingMove error', e);
    }
}

/**
 * Fully clears a pending move entry — used by the revert/cleanup timers and
 * can be called manually if needed. Safe to call when no entry exists.
 *
 * @param {string|number} participantId
 */
export function removePendingMove(participantId) {
    try {
        const pid = String(participantId);
        const entry = _moves[pid];
        if (!entry) return;

        if (_timers[pid]) {
            clearTimeout(_timers[pid].revertTimer);
            clearTimeout(_timers[pid].cleanupTimer);
            delete _timers[pid];
        }

        const { sourceSpaceId, targetSpaceId } = entry;
        delete _moves[pid];

        _renderSpace(sourceSpaceId);
        _renderSpace(targetSpaceId);
    } catch (e) {
        console.warn('[PendingMoves] removePendingMove error', e);
    }
}

/**
 * Returns the sets of participant IDs that are optimistically moving INTO or
 * OUT OF a given space.
 *
 * @param {string|number} spaceId
 * @returns {{ incomingPids: string[], outgoingPids: string[] }}
 */
export function getPendingMovesForSpace(spaceId) {
    const sid = String(spaceId);
    const incomingPids = [];
    const outgoingPids = [];
    Object.values(_moves).forEach(entry => {
        if (String(entry.targetSpaceId) === sid) {
            incomingPids.push(entry.participantId);
        }
        if (String(entry.sourceSpaceId) === sid) {
            outgoingPids.push(entry.participantId);
        }
    });
    return { incomingPids, outgoingPids };
}

/**
 * Called when a participant is observed JOINING a space. Confirms the move when they
 * reached the pending target, and drops the optimistic state when they went anywhere
 * else, so the tiles stop lying before the 6 s revert timer.
 *
 * @param {string|number} participantId
 * @param {string|number} spaceId 'lobby' or a space id
 */
export function reconcileOnJoin(participantId, spaceId) {
    try {
        const pid = String(participantId);
        const entry = _moves[pid];
        if (!entry) return;
        if (entry.targetSpaceId === String(spaceId)) {
            confirmPendingMove(pid);
        } else {
            removePendingMove(pid);
        }
    } catch (e) {
        console.warn('[PendingMoves] reconcileOnJoin error', e);
    }
}

/**
 * Called when a participant is observed LEAVING a space. Leaving the pending target voids
 * the move; leaving the source of a move-to-lounge means it succeeded, because the lounge
 * never produces a join echo.
 *
 * @param {string|number} participantId
 * @param {string|number} spaceId the space that was just left
 */
export function reconcileOnLeave(participantId, spaceId) {
    try {
        const pid = String(participantId);
        const entry = _moves[pid];
        if (!entry) return;
        const sid = String(spaceId);
        if (entry.targetSpaceId === sid) {
            removePendingMove(pid);
            return;
        }
        if (entry.targetSpaceId === 'lobby' && entry.sourceSpaceId === sid) {
            confirmPendingMove(pid);
        }
    } catch (e) {
        console.warn('[PendingMoves] reconcileOnLeave error', e);
    }
}
